import { useState, useMemo, useCallback } from "react";

import { produce } from "immer";

import type { IImportedKeywords } from "../../keywords-edit/text-area/types";

import type { IKeywordsStats } from "./types";

interface IKeywordsState {
    manualKeywords: Set<string>;
    manualKeywordsRaw: string[];
    importSources: Record<string, IImportedKeywords>;
}

interface IUseKeywordsManagementReturn {
    manualKeywords: string[];
    manualKeywordsRaw: string[];
    setManualKeywords: (keywords: string[]) => void;
    importSources: IImportedKeywords[];
    addImportSource: (imported: IImportedKeywords) => void;
    removeImportSource: (importId: string) => void;
    deduplicatedKeywords: IKeywordsStats;
    resetKeywords: () => void;
    hasManualKeywords: boolean;
}

const initialState: IKeywordsState = {
    manualKeywords: new Set<string>(),
    manualKeywordsRaw: [],
    importSources: {},
};

export const useKeywordsManagement = (): IUseKeywordsManagementReturn => {
    const [state, setState] = useState<IKeywordsState>(initialState);

    const manualKeywordsArray = useMemo(() => Array.from(state.manualKeywords), [state.manualKeywords]);

    const importSourcesArray = useMemo(() => Object.values(state.importSources), [state.importSources]);

    const deduplicatedKeywords = useMemo((): IKeywordsStats => {
        const keywordSources: Record<string, string> = {};
        const uniqueKeywords: string[] = [];
        let totalInputKeywords = 0;

        totalInputKeywords += state.manualKeywordsRaw.length;

        manualKeywordsArray.forEach((keyword) => {
            const normalizedKeyword = keyword.toLowerCase().trim();
            if (normalizedKeyword && !keywordSources[normalizedKeyword]) {
                keywordSources[normalizedKeyword] = "manual";
                uniqueKeywords.push(normalizedKeyword);
            }
        });

        importSourcesArray.forEach((source) => {
            totalInputKeywords += source.keywords.length;
            source.keywords.forEach((keyword) => {
                const normalizedKeyword = keyword.toLowerCase().trim();
                if (normalizedKeyword && !keywordSources[normalizedKeyword]) {
                    keywordSources[normalizedKeyword] = source.id;
                    uniqueKeywords.push(normalizedKeyword);
                }
            });
        });

        const hasDuplicates = totalInputKeywords > uniqueKeywords.length;

        return {
            totalCount: uniqueKeywords.length,
            uniqueKeywords,
            keywordSources,
            hasDuplicates,
        };
    }, [manualKeywordsArray, importSourcesArray, state.manualKeywordsRaw]);

    const setManualKeywords = useCallback((keywords: string[]) => {
        setState(
            produce((draft) => {
                const processedKeywords = keywords
                    .map((keyword) => keyword.toLowerCase().trim())
                    .filter((keyword) => keyword.length > 0);
                draft.manualKeywordsRaw = processedKeywords;
                draft.manualKeywords = new Set(processedKeywords);
            }),
        );
    }, []);

    const addImportSource = useCallback((importedKeywords: IImportedKeywords) => {
        setState(
            produce((draft) => {
                draft.importSources[importedKeywords.id] = importedKeywords;
            }),
        );
    }, []);

    const removeImportSource = useCallback((importId: string) => {
        setState(
            produce((draft) => {
                delete draft.importSources[importId];
            }),
        );
    }, []);

    const resetKeywords = useCallback(() => {
        setState(initialState);
    }, []);

    return {
        manualKeywords: manualKeywordsArray,
        manualKeywordsRaw: state.manualKeywordsRaw,
        setManualKeywords,
        importSources: importSourcesArray,
        addImportSource,
        removeImportSource,
        deduplicatedKeywords,
        resetKeywords,
        hasManualKeywords: state.manualKeywords.size > 0,
    };
};
